import {useEffect, useState} from 'react';
import { getBomModalFields } from '../components/BomList/Modal/BomModal';
import {useMaterialData} from "../../MaterialManagement/hooks/useMaterialData";

/**
 * BOM 모달 상태 및 핸들러 제공 훅
 *
 * @param {Object} params - 훅 파라미터
 * @param {Object} params.bomData - BOM 관련 데이터/함수
 * @param {Function} params.getMaterialOptions - 자재 옵션을 가져오는 함수
 * @param {Function} params.getMaterialDetails - 자재 상세 정보를 가져오는 함수
 * @param {Function} params.getMaterialsByType - 자재 타입별 필터링 함수
 * @param {Function} params.handleBomSave - BOM 저장 핸들러
 * @returns {Object} 모달 관련 상태 및 함수
 */
export const useBomModal = ({
                                bomData,
                                getMaterialOptions,
                                getMaterialDetails,
                                getMaterialsByType,
                                handleBomSave,
                                executeQuery
                            }) => {
    // BOM 모달 필드 가져오기
    const BOM_MODAL_FIELDS = getBomModalFields(getMaterialOptions, getMaterialDetails);

    // 모달 상태 관리
    const [modalConfig, setModalConfig] = useState({
        open: false,
        modalType: '',
        title: '',
        size: 'sm',
        values: {},
        fields: BOM_MODAL_FIELDS
    });

    // Material 데이터 훅
    const { materials, loadBOMMaterials, loadMaterials } = useMaterialData(executeQuery);

    // materials 상태 변경 시 모달 드롭다운 옵션 자동 갱신
    useEffect(() => {
        if (modalConfig.open && modalConfig.values.materialType) {
            const materialType = modalConfig.values.materialType;
            // getMaterialsByType 대신 materials 상태를 직접 필터링
            const latestMaterials = materials.filter(m => m.materialType === materialType);
            const options = latestMaterials.map(item => ({
                value: item.systemMaterialId,
                label: item.materialName
            }));

            setModalConfig(prev => ({
                ...prev,
                fields: prev.fields.map(f => {
                    if (f.id === 'systemMaterialId') {
                        return {
                            ...f,
                            options: [...options],
                            disabled: prev.modalType === 'edit'
                        };
                    }
                    return { ...f };
                })
            }));
        }
    }, [materials, modalConfig.open, modalConfig.values.materialType]);

    // 모달 필드 변경 핸들러
    const handleModalFieldChange = async (fieldId, value) => {
        const field = BOM_MODAL_FIELDS.find(f => f.id === fieldId);
        if (!field) return;

        // 먼저 값을 설정
        setModalConfig(prev => ({
            ...prev,
            values: {
                ...prev.values,
                [fieldId]: value
            }
        }));

        // 제품 선택 시 자동입력 처리
        if (fieldId === 'systemMaterialId' && value) {
            const selectedMaterial = materials.find(m => m.systemMaterialId === value);
            if (selectedMaterial) {
                setModalConfig(prev => ({
                    ...prev,
                    values: {
                        ...prev.values,
                        systemMaterialId: value,
                        userMaterialId: selectedMaterial.userMaterialId || '',
                        materialStandard: selectedMaterial.materialStandard || '',
                        unit: selectedMaterial.unit || ''
                    }
                }));
                return; // 자동입력 완료 후 relation 처리 건너뛰기
            }
        }

        if (field?.relation) {
            try {
                const { targetField, getOptions, onSelect } = field.relation;

                if (targetField && getOptions) {
                    // getMaterialOptions 함수 대신 materials 상태를 직접 사용
                    if (fieldId === 'materialType') {
                        const filteredMaterials = materials.filter(m => m.materialType === value);
                        const options = filteredMaterials.map(item => ({
                            value: item.systemMaterialId,
                            label: item.materialName
                        }));
                        
                        setModalConfig(prev => ({
                            ...prev,
                            fields: prev.fields.map(f => {
                                if (f.id === targetField) {
                                    return {
                                        ...f,
                                        options,
                                        disabled: prev.modalType === 'edit'
                                    };
                                }
                                return f;
                            })
                        }));
                    } else {
                        const options = getOptions(value);
                        setModalConfig(prev => ({
                            ...prev,
                            fields: prev.fields.map(f => {
                                if (f.id === targetField) {
                                    return {
                                        ...f,
                                        options,
                                        disabled: prev.modalType === 'edit'
                                    };
                                }
                                return f;
                            })
                        }));
                    }
                } else if (onSelect && value) {
                    const additionalValues = onSelect(value);
                    setModalConfig(prev => ({
                        ...prev,
                        values: {
                            ...prev.values,
                            ...additionalValues
                        }
                    }));
                }
            } catch (error) {
                console.error('관계성 필드 처리 중 오류:', error);
            }
        }
    };

    // 모달 저장 핸들러
    const handleModalSubmit = async () => {
        try {
            await handleBomSave(modalConfig.values);
            handleCloseModal();
        } catch (error) {
            console.error('저장 실패:', error);
        }
    };

    // 등록 모달 열기
    const handleOpenRegisterModal = () => {
        setModalConfig({
            open: true,
            modalType: 'register',
            title: 'BOM 등록',
            values: {
                bomLevel: 1
            },
            fields: BOM_MODAL_FIELDS
        });
    };

    // 수정 모달 열기
    const handleOpenEditModal = async () => {
        if (!bomData.selectedBom) return;

        // materialType에 맞는 제품 목록을 materials 상태에서 직접 필터링
        try {
            const filteredMaterials = materials.filter(m => m.materialType === bomData.selectedBom.materialType);

            const options = filteredMaterials.map(item => ({
                value: item.systemMaterialId,
                label: item.materialName
            }));

            // 모달 상태 설정
            setModalConfig({
                open: true,
                modalType: 'edit',
                title: 'BOM 수정',
                values: {
                    ...bomData.selectedBom,
                    systemMaterialId_options: options,
                    systemMaterialId_raw_data: filteredMaterials
                },
                fields: BOM_MODAL_FIELDS.map(field => {
                    if (field.id === 'systemMaterialId') {
                        return {
                            ...field,
                            options,
                            disabled: true
                        };
                    }
                    if (field.id === 'materialType') {
                        return {
                            ...field,
                            disabled: true
                        };
                    }
                    return field;
                })
            });
        } catch (error) {
            console.error('제품 목록 로드 실패:', error);
        }
    };

    // 모달 닫기
    const handleCloseModal = () => {
        setModalConfig({
            open: false,
            modalType: '',
            title: '',
            values: {},
            fields: BOM_MODAL_FIELDS
        });
    };

    // 옵션 새로고침 로직 - useEffect가 자동으로 드롭다운 갱신
    const handleRefreshMaterialOptions = async () => {
        try {
            // 전체 자재 데이터를 다시 로드하여 전역 상태 업데이트
            await loadMaterials();
            // materials 상태가 변경되면 useEffect가 자동으로 드롭다운 옵션 갱신
        } catch (error) {
            console.error('자재 옵션 새로고침 실패:', error);
        }
    };


    return {
        materials,
        modalConfig,
        handleModalFieldChange,
        handleModalSubmit,
        handleOpenRegisterModal,
        handleOpenEditModal,
        handleCloseModal,
        handleRefreshMaterialOptions
    };
};